<script lang="ts" setup>
	import type { filterCollectionSchema } from '~/components/Forms/filter.vue'

	export type ActivitySchema = {
		name: string
		duration: string
		status: string
	}
	export type ActivityHistoryContext = {
		label: string
		data: {
			today: ActivitySchema[]
			yesterday: ActivitySchema[]
			two_days: ActivitySchema[]
			last_week: ActivitySchema[]
			older: ActivitySchema[]
		}
	}[]

	defineProps<{
		showTotalLabel?: boolean
		filterList: filterCollectionSchema[]
		filterBy: string | number
		histories: ActivityHistoryContext
		label: { [id: string]: number | string }
		amountUsed?: number
		totalClaims?: number
		isLoading?: boolean
	}>()

	const { internationalNoFormat } = useCurrency()
	const { formatDate } = useCustomDate()
	defineEmits<{ (event: 'update:filterBy', param: string): void }>()
</script>

<template>
	<div>
		<div
			class="mi-h-[50px] mb-8 flex items-center justify-around gap-x-3 rounded border border-gray-100 bg-gray-50 py-3.5 tracking-wide"
		>
			<div
				v-for="(item, k) in label"
				:key="k"
				class="inline-flex flex-1 items-center gap-x-1 border-gray-200 px-5 first:border-r"
			>
				<span class="text-sm text-gray-500"> {{ k }}</span>
				<span class="font-medium text-gray-600">{{ item }}</span>
			</div>
		</div>

		<div class="flex flex-wrap items-center justify-between gap-3">
			<div
				class="mb-3 items-center gap-1 rounded border border-success-200 bg-success-50 px-4 py-2.5"
				:class="{ 'opacity-0': !showTotalLabel, 'inline-flex opacity-100': showTotalLabel }"
			>
				<span class="text-xs text-gray-500"> {{ amountUsed ? 'Amount Used:' : 'Total Claims Paid:' }}</span>
				<span class="max-w-sm overflow-hidden text-ellipsis text-sm font-medium text-gray-700">
					₦{{ internationalNoFormat(amountUsed || totalClaims || 0) }}
				</span>
			</div>

			<forms-filter
				:filter-by="filterBy"
				:filter-collection="filterList"
				class="!rounded border px-2 py-[6px]"
				:use-default-class="false"
				@update:filter-by="e => $emit('update:filterBy', e)"
			>
				<template #active-text>
					<div class="flex items-center gap-x-2">
						<lazy-tools-icon
							icon="note"
							dir="documents"
							:height="16"
							:width="16"
							path-class="stroke-gray-950 fill-transparent"
						/>
						<span>{{ filterBy }}</span>
					</div>
				</template>
			</forms-filter>
		</div>

		<loader-skeleton
			v-if="isLoading"
			:count="4"
			class-list="mt-4 space-y-3 w-full"
			:max-width="false"
			:height="30"
		/>

		<ul v-else class="custom-scrollbar mt-5 max-h-[400px] overflow-y-auto tracking-wide">
			<li
				v-for="(item, index) in histories"
				:key="index"
				class="border-b border-gray-200 py-5 text-xs font-medium text-gray-500 first:pt-0 last:border-b-0"
			>
				<h6 class="border-l-2 border-primary-2 px-2 font-semibold text-gray-600">
					{{ item?.label }}
				</h6>

				<template v-for="(records, relativeTime) in item.data" :key="relativeTime">
					<div v-if="records && records.length > 0">
						<div class="flex items-center gap-x-2 py-4 font-semibold capitalize">
							<span class="text-gray-900">{{ String(relativeTime).replace('_', ' ') }}</span> |
							<span class="text-xs font-medium text-gray-600">{{
								formatDate(records[0]?.duration ?? '', 'DD MMM, YYYY')
							}}</span>
						</div>

						<template v-for="(history, i) in records" :key="i">
							<div class="mb-4 flex w-full items-center justify-between">
								<div class="flex items-center gap-x-3 font-normal">
									<div
										class="flexed h-[20px] w-[20px] flex-none items-center rounded-full bg-gray-100"
									>
										{{ history.name?.charAt(0) ?? 'U' }}
									</div>
									<div class="inline-flex items-center gap-x-2 font-medium">
										<span>{{ history?.name || 'N/A' }}</span>
										<div class="h-[4px] w-[4px] rounded-full bg-gray-200" />
										<span>{{ formatDate(history.duration, 'hh:mm a') || 'N/A' }}</span>
									</div>
								</div>
								<wellness-status :status="history.status" />
							</div>
						</template>
					</div>
				</template>
			</li>
		</ul>
	</div>
</template>
